import React, { useContext } from "react";
import { PhotoManagerContext } from "./index";

const redactedIcon: string = require("../styles/images/redactedIcon.svg");
const photoLockedIcon: string = require("../styles/images/photo-locked.svg");
const lockedRedactedIcon: string = require("../styles/images/locked-redacted.svg");
const hiddenIcon: string = require("../styles/images/hide.svg");
const warningIcon: string = require("../styles/images/warning.svg");

interface PhotoVisibilityIconProps {
  photo: any;
  showActionTooltip: (id: number, title: string, type: string) => void;
}

const PhotoVisibilityIcon: React.FC<PhotoVisibilityIconProps> = ({
  photo,
  showActionTooltip
}) => {
  const context = useContext(PhotoManagerContext);
  const isRedactionPending =
    context?.redactionCheckEnabled && !photo.redactionChecked;

  // TODO: Update these conditions when photo permissions is implemented
  const isRedactedAndLocked = false;
  const isOnlyLocked = false;

  const isFlagged = photo.flaggedForModeration === true;
  const isOnlyRedacted = !isFlagged && (isRedactionPending || photo.redacted);

  const shouldShowIcon = photo.hidden || photo.redacted || isRedactionPending || isFlagged;

  if (!shouldShowIcon) return null;

  const TOOLTIP_MESSAGES = {
    REDACTED_AND_LOCKED:
      "<b>Redacted, visible only to you.</b><br/><small>Manage redacted photos in <br/> Settings > Photo security</small> <br/><b>Permissions Applied.</b>",
    PERMISSIONS_APPLIED: "Permissions Applied",
    REDACTION_PENDING:
      "Redaction review in progress, which may take up to one hour before your photo is available in published galleries.",
    REDACTED:
      "<b>Redacted, visible only to you.</b><br/><small>Manage redacted photos in Settings > Photo security</small>",
    HIDDEN: "Hidden, visible only to you",
    FLAGGED: "Inappropriate content detected, awaiting admin confirmation"
  };

  const tooltipTitle = isRedactedAndLocked
    ? TOOLTIP_MESSAGES.REDACTED_AND_LOCKED
    : isOnlyLocked
    ? TOOLTIP_MESSAGES.PERMISSIONS_APPLIED
    : isFlagged
    ? TOOLTIP_MESSAGES.FLAGGED
    : isRedactionPending
    ? TOOLTIP_MESSAGES.REDACTION_PENDING
    : photo.redacted
    ? TOOLTIP_MESSAGES.REDACTED
    : TOOLTIP_MESSAGES.HIDDEN;

  const iconSrc = isRedactedAndLocked
    ? lockedRedactedIcon
    : isOnlyLocked
    ? photoLockedIcon
    : isFlagged
    ? warningIcon
    : isOnlyRedacted
    ? redactedIcon
    : hiddenIcon;

  // Flagged photos use native title attribute since the grid overlay prevents
  // Bootstrap tooltip positioning. Other states use Bootstrap tooltips via showActionTooltip.
  const handleTooltipTrigger = () => {
    if (!isFlagged) {
      showActionTooltip(photo.id, tooltipTitle, "hide-redacted");
    }
  };

  const iconClassName = `photo-status-icon ${
    isRedactionPending && !isFlagged ? "blinking-redaction" : ""
  }`;

  return (
    <span
      id={`hide-redacted-icon-${photo.id}`}
      className="ws-visibility-stamp"
      data-toggle={isFlagged ? undefined : "tooltip"}
      data-placement="top"
      data-html="true"
      title={isFlagged ? tooltipTitle : undefined}
      onMouseEnter={handleTooltipTrigger}
      onFocus={handleTooltipTrigger}
      tabIndex={0}
      role="status"
      aria-label={tooltipTitle.replace(/<br\/><small>.*<\/small>/, "")}
    >
      <img
        src={iconSrc}
        alt="Photo visibility status icon"
        className={iconClassName}
      />
    </span>
  );
};

export default PhotoVisibilityIcon;
